'use client';

import { c as _c } from "react/compiler-runtime";
import { NexperienceTimeslotsDocument } from '@data-access/lib/gql/experience/documents';
import { useGraphQL } from '@data-access/lib/graphql-query';
import { parseLocalDay } from '@utils/helpers';
import { useAtomValue } from 'jotai';
import { useExperienceFilterContext } from '../_components/app-experience-filter-provider';
import { edpTimeslotSelectionAtom } from '../_lib/edp-booking-atoms';
import { getEdpTimeslotSelectionScope } from '../_lib/edp-timeslot-selection';
import { EXPERIENCE_AVAILABILITY_STALE_TIME, getExperienceTimeslotsBaseVariables } from '../_lib/experience-availability-query';
import { mapDatesToEdpTimeslots } from '../_lib/experience-booking-utils';
import { useAppExperienceDayPrices } from './use-app-experience-day-prices';
import type { useAppExperienceTimeslotsPreview } from './use-app-experience-timeslots-preview';
import { useExperiencePreviewToken } from './use-experience-preview-token';
type TimeslotsPreview = Pick<ReturnType<typeof useAppExperienceTimeslotsPreview>, 'allTimeslots' | 'prices' | 'isLoading' | 'isPlaceholderData' | 'isError'>;
export function useSelectedEdpTimeslot(preview) {
  const $ = _c(35);
  const {
    experience,
    baseVariants,
    filter
  } = useExperienceFilterContext();
  const storedSelection = useAtomValue(edpTimeslotSelectionAtom);
  const scope = getEdpTimeslotSelectionScope(experience.id, filter);
  const selection = storedSelection?.scope === scope ? storedSelection : null;
  const previewToken = useExperiencePreviewToken();
  const previewUnavailable = preview.isLoading || preview.isPlaceholderData || preview.isError;
  let t0;
  if ($[0] !== preview.allTimeslots || $[1] !== selection?.id) {
    let t1;
    if ($[3] !== selection?.id) {
      t1 = slot => slot.id === selection?.id;
      $[3] = selection?.id;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = preview.allTimeslots.find(t1);
    $[0] = preview.allTimeslots;
    $[1] = selection?.id;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const previewTimeslot = t0;
  const resolveDay = !!selection && !previewUnavailable && !previewTimeslot;
  const day = resolveDay ? selection.date : undefined;
  let t1;
  if ($[5] !== day || $[6] !== experience.publicId || $[7] !== filter || $[8] !== previewToken) {
    const date = day ? parseLocalDay(day) : null;
    t1 = getExperienceTimeslotsBaseVariables({
      publicId: experience.publicId,
      previewToken,
      filter: {
        ...filter,
        from: date,
        to: date
      }
    });
    $[5] = day;
    $[6] = experience.publicId;
    $[7] = filter;
    $[8] = previewToken;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const variables = t1;
  let t2;
  if ($[10] !== resolveDay) {
    t2 = {
      enabled: resolveDay,
      select: _temp,
      staleTime: EXPERIENCE_AVAILABILITY_STALE_TIME
    };
    $[10] = resolveDay;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const availability = useGraphQL(NexperienceTimeslotsDocument, variables, t2);
  const prices = useAppExperienceDayPrices(experience.id, day, day);
  const availabilityFailed = resolveDay && (availability.isError || availability.isFetching && availability.errorUpdatedAt > availability.dataUpdatedAt);
  const priceFailed = resolveDay && prices.isError;
  const isPending = resolveDay && (availability.isLoading || prices.arePreliminary);
  let timeslot;
  if ($[12] !== availability.data || $[13] !== availabilityFailed || $[14] !== baseVariants || $[15] !== filter || $[16] !== isPending || $[17] !== preview.prices || $[18] !== previewTimeslot || $[19] !== previewUnavailable || $[20] !== priceFailed || $[21] !== prices.dayPrices || $[22] !== resolveDay || $[23] !== selection) {
    let t3;
    if ($[25] !== filter) {
      t3 = option => Object.hasOwn(filter.selectedOptions ?? {}, option.id);
      $[25] = filter;
      $[26] = t3;
    } else {
      t3 = $[26];
    }
    const options = baseVariants.flatMap(_temp2).filter(t3);
    timeslot = null;
    if (selection && !previewUnavailable && !preview.prices.arePreliminary && !preview.prices.isError && !availabilityFailed && !priceFailed && !isPending) {
      const dayTimeslots = resolveDay ? mapDatesToEdpTimeslots(availability.data ?? [], options, filter.selectedOptions, prices.dayPrices) : [];
      timeslot = previewTimeslot ?? dayTimeslots.find(slot_0 => slot_0.id === selection.id) ?? null;
    }
    $[12] = availability.data;
    $[13] = availabilityFailed;
    $[14] = baseVariants;
    $[15] = filter;
    $[16] = isPending;
    $[17] = preview.prices;
    $[18] = previewTimeslot;
    $[19] = previewUnavailable;
    $[20] = priceFailed;
    $[21] = prices.dayPrices;
    $[22] = resolveDay;
    $[23] = selection;
    $[24] = timeslot;
  } else {
    timeslot = $[24];
  }
  const t3 = resolveDay && (availability.isFetching || prices.isFetching);
  let t4;
  if ($[27] !== availability.refetch || $[28] !== availabilityFailed || $[29] !== isPending || $[30] !== priceFailed || $[31] !== prices.refetch || $[32] !== t3 || $[33] !== timeslot) {
    t4 = {
      timeslot,
      isPending,
      availabilityFailed,
      priceFailed,
      isFetching: t3,
      refetchAvailability: availability.refetch,
      refetchPrices: prices.refetch
    };
    $[27] = availability.refetch;
    $[28] = availabilityFailed;
    $[29] = isPending;
    $[30] = priceFailed;
    $[31] = prices.refetch;
    $[32] = t3;
    $[33] = timeslot;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  return t4;
}
function _temp2(variant) {
  return variant.options;
}
function _temp(data) {
  return data.nexperienceAvailableDates.dates;
}